<template>
  <div>
    <h2 class="text-h5 mb-4">
      Клиенты
    </h2>

    <v-row dense class="mb-4">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="search"
          label="Поиск (телефон, имя, email)"
          dense
          outlined
          hide-details
          clearable
          @keyup.enter="load(1)"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="filters.attribution"
          :items="[
            { text: 'Lead Touch', value: 'lead' },
            { text: 'First Touch', value: 'first' },
          ]"
          label="Атрибуция"
          dense
          outlined
          hide-details
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="3" class="d-flex align-center">
        <v-btn color="primary" @click="load(1)">
          Найти
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="5">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page="pageSize"
          hide-default-footer
          class="elevation-0"
          @click:row="openCustomer"
        >
          <template #item.first_source="{ item }">
            <span :class="{ 'grey--text': item.first_source === 'direct / unknown' }">
              {{ item.first_source }}
            </span>
          </template>
          <template #item.revenue="{ item }">
            {{ formatMoney(item.revenue) }} ₽
          </template>
        </v-data-table>
        <div class="d-flex justify-space-between align-center mt-2">
          <span class="text-caption">Всего: {{ total }}</span>
          <div>
            <v-btn
              small
              text
              :disabled="page <= 1"
              @click="load(page - 1)"
            >
              Назад
            </v-btn>
            <span class="mx-2 text-caption">{{ page }}</span>
            <v-btn
              small
              text
              :disabled="page * pageSize >= total"
              @click="load(page + 1)"
            >
              Вперёд
            </v-btn>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="7">
        <v-card
          v-if="card"
          outlined
          class="pa-4"
        >
          <div class="d-flex justify-space-between align-start mb-2">
            <div>
              <h3 class="text-h6 mb-1">
                {{ card.name || 'Без имени' }}
              </h3>
              <div class="text-body-2">
                {{ card.phone }}
                <span v-if="card.email"> · {{ card.email }}</span>
              </div>
              <div class="text-caption grey--text">
                customer_id: {{ card.customer_id }}
              </div>
            </div>
            <div class="text-right">
              <div class="text-caption grey--text">
                Выручка
              </div>
              <div class="text-h6">
                {{ formatMoney(card.revenue) }} ₽
              </div>
            </div>
          </div>

          <v-alert
            v-if="card.needs_manual_review"
            type="warning"
            dense
            text
            class="mb-3"
          >
            Требует ручного разбора склейки
          </v-alert>

          <v-row dense class="mb-3">
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">
                First Touch
              </div>
              <div>
                {{ card.first_touch.source || 'direct / unknown' }}
                <span v-if="card.first_touch.campaign">
                  / {{ card.first_touch.campaign }}
                </span>
              </div>
              <div class="text-caption">
                {{ card.first_touch.at || '—' }}
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">
                Lead Touch (последний)
              </div>
              <div v-if="card.lead_touches && card.lead_touches.length">
                {{ card.lead_touches[0].source }}
                <span v-if="card.lead_touches[0].campaign">
                  / {{ card.lead_touches[0].campaign }}
                </span>
              </div>
              <div
                v-else
                class="grey--text"
              >
                —
              </div>
            </v-col>
          </v-row>

          <div
            v-if="card.yclients && card.yclients.length"
            class="mb-3 text-caption"
          >
            YClients:
            <span
              v-for="(yc, i) in card.yclients"
              :key="i"
            >
              {{ yc.yc_client_id }}@{{ yc.company_id }}<span v-if="i < card.yclients.length - 1">, </span>
            </span>
          </div>

          <h4 class="subtitle-1 mb-2">
            Хронология
          </h4>
          <v-timeline
            dense
            align-top
            class="ml-n2"
          >
            <v-timeline-item
              v-for="(ev, idx) in card.timeline"
              :key="idx"
              small
              :color="timelineColor(ev.type)"
            >
              <div class="text-caption grey--text">
                {{ ev.at }}
              </div>
              <div class="font-weight-medium">
                {{ timelineLabel(ev) }}
              </div>
              <div
                v-if="ev.amount != null"
                class="text-body-2"
              >
                {{ formatMoney(ev.amount) }} ₽
                <span v-if="ev.title">— {{ ev.title }}</span>
              </div>
              <div
                v-else-if="ev.title"
                class="text-body-2"
              >
                {{ ev.title }}
              </div>
              <div
                v-if="ev.source"
                class="text-caption"
              >
                источник: {{ ev.source }}
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card>
        <v-card
          v-else
          outlined
          class="pa-6 text-center grey--text"
        >
          Выберите клиента слева
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Api from '@/api/api'

export default {
  name: 'AnalyticsClientCard',
  data() {
    return {
      search: '',
      filters: { attribution: 'lead' },
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      card: null,
      headers: [
        { text: 'Имя', value: 'name' },
        { text: 'Телефон', value: 'phone' },
        { text: 'First', value: 'first_source' },
        { text: 'Выручка', value: 'revenue', align: 'end' },
      ],
    }
  },
  mounted() {
    const qid = this.$route.query.customer_id
    this.load(1).then(() => {
      if (qid) this.openCustomer({ customer_id: qid })
    })
  },
  methods: {
    formatMoney(v) {
      return Number(v || 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 0,
      })
    },
    timelineColor(type) {
      const map = {
        page_view: 'grey',
        booking_form_open: 'blue',
        booking_form_submit: 'indigo',
        booking_created: 'teal',
        club_visit: 'green',
        no_show: 'orange',
        purchase: 'purple',
      }
      return map[type] || 'grey'
    },
    timelineLabel(ev) {
      const map = {
        page_view: 'Визит на сайт',
        booking_form_open: 'Открыл форму',
        booking_form_submit: 'Отправил форму',
        booking_created: 'Запись в YClients',
        club_visit: 'Пришёл',
        no_show: 'Неявка',
        purchase: 'Покупка',
      }
      return map[ev.type] || ev.type
    },
    async load(page) {
      this.page = page || 1
      this.loading = true
      try {
        const data = await Api.getAnalyticsCustomers({
          search: this.search || undefined,
          attribution: this.filters.attribution,
          page: this.page,
          page_size: this.pageSize,
        })
        this.items = data.items || []
        this.total = data.total || 0
      } catch (e) {
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async openCustomer(item) {
      if (!item || !item.customer_id) return
      this.$router.replace({
        query: { ...this.$route.query, customer_id: item.customer_id },
      }).catch(() => {})
      try {
        this.card = await Api.getAnalyticsCustomer(item.customer_id)
      } catch (e) {
        this.card = null
      }
    },
  },
}
</script>
